/**
 * The shadow answer check, rendered as DIAGNOSTICS — never as a verdict.
 *
 * The three numbers are uncalibrated probabilities from a model that saw the
 * question, the prose and the tool descriptors. In shadow mode nothing acted on
 * them. The label says so in the first line, because an operator reads a number
 * as a judgement unless told otherwise. The operator's OWN judgement is the
 * thumbs beside the answer; that is what calibrates these numbers later.
 */
import { FlaskConical } from 'lucide-react';
import type { AnswerCheckSummary } from '../protocol';

type NoulId = keyof NonNullable<AnswerCheckSummary['nouls']>;

const LABELS: Record<NoulId, string> = {
  addresses_question: 'Answers the question asked',
  subject_and_window_match: 'Same population and window',
  claims_beyond_tools: 'Claims beyond the tools',
};

export function AnswerCheckDiagnostics({ check }: { check: AnswerCheckSummary }) {
  const nouls = check.status === 'ok' ? check.nouls : undefined;
  return (
    <details
      className="rounded-md border bg-muted/40 px-2 py-1 text-xs text-muted-foreground"
      data-testid="answer-check-diagnostics"
    >
      <summary className="flex cursor-pointer select-none items-center gap-2">
        <FlaskConical className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Answer check · shadow · not calibrated · does not change the answer
          {check.status === 'skipped' && check.reason ? ` · skipped: ${check.reason}` : ''}
        </span>
      </summary>
      {nouls && (
        <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
          {(Object.keys(LABELS) as NoulId[]).map((id) => (
            <div key={id} className="contents">
              <dt>{LABELS[id]}</dt>
              <dd className="tabular-nums" data-testid={`answer-check-${id}`}>
                {nouls[id].toFixed(2)}
              </dd>
            </div>
          ))}
          <dt>Model</dt>
          <dd className="tabular-nums">{check.model ?? '—'}</dd>
          {typeof check.latency_ms === 'number' && (
            <>
              <dt>Latency</dt>
              <dd className="tabular-nums">{check.latency_ms} ms</dd>
            </>
          )}
        </dl>
      )}
    </details>
  );
}
